/* eslint-disable no-multi-spaces */
/* eslint-disable key-spacing */
import round from 'lodash/round'
import toUpper from 'lodash/toUpper'
import isFinite from 'lodash/isFinite'
import get from 'lodash/get'
import { useContext } from 'react'

import { R } from '@utils/currency'
import { parseNumber } from '@utils/numberConversions'

import { AssetsContext } from '../../context'
import { RETIREMENT_FUNDING_ASSETS, interestRateIsWithinReasonableBounds } from '../../utils/assetServices'
import { INTEREST_RATE_CONTROL } from './AssetFields'
import { planInflationRate } from './annualIncrease'

const ASSET_EVENT_ACTIONS = {
  CHANGE_GROSS_MONTHLY_INCOME: 'change_gross_monthly_income',
  CHANGE_MONTHLY_CONTRIBUTION: 'change_monthly_contribution',
  MATURE_RETIREMENT_ASSET: 'mature_retirement_asset'
}

export const buildInitialValues = () => {
  const { state } = useContext(AssetsContext)
  const { asset, formAssetType, riskProfile, scenario, assumptions, userDetails } = state
  const { retirementAge } = scenario
  const { twoPotOptional } = userDetails

  const returnRate = parseNumber(riskProfile.rating) + parseNumber(assumptions.inflation) * 100

  const changeGrossIncomeEvent = getAssetEventFromAsset(asset, ASSET_EVENT_ACTIONS.CHANGE_GROSS_MONTHLY_INCOME)
  const changeMonthlyContributionEvent = getAssetEventFromAsset(asset, ASSET_EVENT_ACTIONS.CHANGE_MONTHLY_CONTRIBUTION)
  const matureRetirementAssetEvent = getAssetEventFromAsset(asset, ASSET_EVENT_ACTIONS.MATURE_RETIREMENT_ASSET)

  const vestedPot = asset?.assets?.find((asset) => asset.asset_type === 'vested_pot')
  const savingsPot = asset?.assets?.find((asset) => asset.asset_type === 'savings_pot')
  const retirementPot = asset?.assets?.find((asset) => asset.asset_type === 'retirement_pot')

  const composition = asset?.composition || getDefaultComposition(twoPotOptional, formAssetType)
  const initialValues = {
    name: asset.name || '',
    gross: isFinite(asset.gross_value) ? asset.gross_value : null,
    assetCurrency: asset.currency || 'zar',
    reviewDate: asset.review_date || null,
    monthlyContribution: isFinite(asset.monthly_contribution) ? asset.monthly_contribution : null,
    employerMonthlyContribution: isFinite(asset.employer_monthly_contribution) ? asset.employer_monthly_contribution : null,
    // A following asset stores no rate, so the slider shows the plan's inflation - the
    // rate actually in force, and the starting point if the box is later unticked.
    annualIncreaseInMonthlyContribution:
      isFinite(asset.annual_increase_in_monthly_contribution)
        ? round(asset.annual_increase_in_monthly_contribution * 100, 2)
        : planInflationRate(assumptions),
    // New assets track the plan's inflation; existing ones keep how they were saved.
    usePlanInflation: asset.annual_increase_follows_plan_inflation !== false,
    monthlyIncome: isFinite(asset.monthly_income) ? asset.monthly_income : null,
    allowAutomatedIncomeCalculation: asset.allow_automated_income_calculation === undefined ? true : asset.allow_automated_income_calculation,
    interestRate: getAssetInterestRate(asset, formAssetType, assumptions, returnRate),
    interestRateControl: INTEREST_RATE_CONTROL[toUpper(asset.interest_rate_control)] || getDefaultInterestRateOptionFor(formAssetType),
    liability: R(asset.liability).value,
    termOfDebt: asset.term_of_debt || 0,
    monthlyProfit: isFinite(asset.monthly_income) ? asset.monthly_income : null,
    domesticEquity: round(asset.domestic_equity * 100, 2) || null,
    domesticProperty: round(asset.domestic_property * 100, 2) || null,
    domesticBonds: round(asset.domestic_bonds * 100, 2) || null,
    domesticCash: round(asset.domestic_cash * 100, 2) || null,
    offshoreEquity: round(asset.offshore_equity * 100, 2) || null,
    offshoreProperty: round(asset.offshore_property * 100, 2) || null,
    offshoreBonds: round(asset.offshore_bonds * 100, 2) || null,
    offshoreCash: round(asset.offshore_cash * 100, 2) || null,
    serviceProviderId: asset.service_provider_id || '',
    contractNumber: asset.contract_number || null,
    composition,
    potBreakdown: {
      isTwoPot: composition === 'two_pot',
      vestedPot: {
        name: 'Vested Pot',
        id: vestedPot?.id,
        gross_value: vestedPot?.gross_value || 0,
        asset_type: 'vested_pot',
        composition: 'component'
      },
      savingsPot: {
        name: 'Savings Pot',
        id: savingsPot?.id,
        gross_value: savingsPot?.gross_value || 0,
        annual_income: R(savingsPot?.annual_income).value,
        asset_type: 'savings_pot',
        composition: 'component'
      },
      retirementPot: {
        name: 'Retirement Pot',
        id: retirementPot?.id,
        gross_value: retirementPot?.gross_value || 0,
        asset_type: 'retirement_pot',
        composition: 'component'
      },
    },
    baseCost: isFinite(asset.base_cost) ? asset.base_cost : null,
    changeGrossIncomeEvent: {
      id: get(changeGrossIncomeEvent, 'id'),
      // The _destroy key is accepted by planner to destroy existing
      // events linked to an asset.
      // Here we set _destroy to true if an event is not found - and when
      // sent as a param to planner, the event will not be created.
      _destroy: !changeGrossIncomeEvent,
      active: !!changeGrossIncomeEvent,
      action: ASSET_EVENT_ACTIONS.CHANGE_GROSS_MONTHLY_INCOME,
      payload: {
        value: get(changeGrossIncomeEvent, 'payload.value', 0),
        metadata: {
          allowCalculationToExceedSpecifiedValue: get(
            changeGrossIncomeEvent,
            'payload.metadata.allowCalculationToExceedSpecifiedValue',
            true
          ),
        }
      },
      when: {
        type: 'retirement'
      }
    },
    changeMonthlyContributionEvent: {
      id: get(changeMonthlyContributionEvent, 'id'),
      // The _destroy key is accepted by planner to destroy existing
      // events linked to an asset.
      // Here we set _destroy to true if an event is not found - and when
      // sent as a param to planner, the event will not be created.
      _destroy: !changeMonthlyContributionEvent,
      active: !!changeMonthlyContributionEvent,
      action: ASSET_EVENT_ACTIONS.CHANGE_MONTHLY_CONTRIBUTION,
      payload: {
        value: get(changeMonthlyContributionEvent, 'payload.value', 0)
      },
      when: {
        type: 'retirement'
      }
    },
    matureRetirementAssetEvent: {
      id: get(matureRetirementAssetEvent, 'id'),
      // The _destroy key is accepted by planner to destroy existing
      // events linked to an asset.
      // Here we set _destroy to true if an event is not found - and when
      // sent as a param to planner, the event will not be created.
      _destroy: !matureRetirementAssetEvent,
      active: !!matureRetirementAssetEvent,
      action: ASSET_EVENT_ACTIONS.MATURE_RETIREMENT_ASSET,
      payload: {
        unit_trust: {
          recipient_id: get(matureRetirementAssetEvent, 'payload.unit_trust.recipient_id', 'default-retirement-unit-trust'),
          show_preview: false
        },
        living_annuity: {
          recipient_id: get(matureRetirementAssetEvent, 'payload.living_annuity.recipient_id', 'default-retirement-living-annuity'),
          show_preview: false
        }
      },
      when: {
        type: 'age',
        value: get(matureRetirementAssetEvent, 'when.value', retirementAge)
      }
    }
  }
  return initialValues
}

const getAssetInterestRate = (asset, assetType, assumptions, returnRate) => {
  const assetInterestRate = asset.interest_rate && parseNumber(asset.interest_rate * 100).toFixed(2)

  if (interestRateIsWithinReasonableBounds(asset)) {
    return assetInterestRate
  } else if (assetInterestRate === 0 && assetType === 'other_income') {
    return assetInterestRate
  } else {
    return defaultInterestRateOnAssetType(assetType, assumptions, returnRate)
  }
}

const defaultInterestRateOnAssetType = (assetType, assumptions, returnRate) => {
  switch (assetType) {
    case 'money_market':
      return round(assumptions.defaultMoneyMarketInterestRate * 100, 2)
    // In most cases, we can never realistically reach the following case.
    // Assets default to 0 for interest rates, and other income allows 0 as an interest rate above.
    case 'other_income':
      return round(assumptions.defaultOtherIncomeInterestRate * 100, 2)
    case 'investment_property':
      return round(assumptions.primeInterestRate * 100, 2)
    default:
      return round(returnRate, 2)
  }
}

export const getLabel = (name, assetType, assumptions, isRetired) => {
  const { state } = useContext(AssetsContext)
  const { userDetails } = state

  const labels = {
    name: 'Give your asset a name',
    gross: assetType === 'other_income' ? 'What is the gross monthly income?' : 'Current value',
    monthlyContribution: assetType === 'investment_property' ? 'Additional monthly repayment' : `${userDetails?.clientPronoun || 'Your'} monthly contribution`,
    employerMonthlyContribution: 'Employer monthly contribution',
    annualIncreaseInMonthlyContribution: 'Annual increase in contribution %',
    monthlyIncome: assetType === 'savings_pot' ? 'How much are you withdrawing annually?' : 'How much are you withdrawing monthly?',
    allowAutomatedIncomeCalculation: 'Allow this asset to withdraw more than specified to cover budget shortfall',
    assetCurrency: 'Select a currency',
    reviewDate: 'Annual Review Date',
    interestRate: interestRateLabel(assetType, assumptions),
    liability: 'Liability value (debt)',
    termOfDebt: 'Term of debt left (years)',
    monthlyProfit: 'Monthly profit / loss (after expenses including bond)',
    contractNumber: 'Contract number',
    baseCost: 'How much has been contributed to this asset?',
    changeGrossIncomeEvent: changeGrossIncomeEventLabel(isRetired),
    changeMonthlyContributionEvent: changeGrossContributionEventLabel(isRetired),
    matureRetirementAssetEvent: 'Choose where the money goes at retirement'
  }
  return labels[name]
}

export const getFieldsToTouch = (fieldName) => {
  const fieldsToTouch = {
    'liability': ['termOfDebt']
  }
  return get(fieldsToTouch, fieldName, [''])
}

const getDefaultInterestRateOptionFor = (assetType) => {
  if (['money_market', 'other_income', 'investment_property'].includes(assetType)) {
    return INTEREST_RATE_CONTROL.BASED_ON_SPECIFIED_INTEREST_RATE
  }
  return INTEREST_RATE_CONTROL.BASED_ON_RISK_PROFILE
}

const interestRateLabel = (assetType, assumptions) => {
  switch (assetType) {
    case 'other_income':
      return 'Annual increase in income? %'
    case 'investment_property':
      return `What is the interest rate on your bond? (Prime = ${round(assumptions.primeInterestRate * 100, 2)}%)`
    default:
      return 'What is the expected return rate? %'
  }
}

const getDefaultComposition = (twoPotOptional, formAssetType) => {
  if (RETIREMENT_FUNDING_ASSETS.includes(formAssetType) && !twoPotOptional) {
    return 'two_pot'
  }
  return 'standalone'
}

const getAssetEventFromAsset = (asset, eventType) => {
  if (!asset.asset_events) {
    return null
  }
  return asset.asset_events.find((asset_event) => asset_event.action === eventType)
}

const changeGrossIncomeEventLabel = (isRetired) =>
  isRetired
    ? 'Set total monthly withdrawal for retirement'
    : 'Set total monthly withdrawal at retirement'

const changeGrossContributionEventLabel = (isRetired) =>
  isRetired
    ? 'Set total monthly contribution for retirement'
    : 'Set total monthly contribution at retirement'
